import { MUICheckBox, MUISelect, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { MouseEvent, useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../services/api"
import { FieldRow, FormPage } from "../../../../components/FormPage"
import {
  ApplicationPropertyPost,
  ApplicationPropertyTypeEnum,
} from "../../../../models/ApplicationProperty"
import { ControllerRendererProps } from "../../../../types/reactHookForm"

const validationSchema = z.object({
  name: z.string().min(1, "Name field is required"),
  type: z
    .union([z.nativeEnum(ApplicationPropertyTypeEnum), z.literal("")])
    .refine((val) => val !== "", { message: "Type field is required" }),
  isRequestable: z.boolean(),
})

export function ApplicationPropertyCreate() {
  const { applicationId } = useParams()
  const navigate = useNavigate()
  const returnURL = `/applications/${applicationId}#properties`

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<ApplicationPropertyPost>({
    resolver: zodResolver(validationSchema),
  })

  useEffect(() => {
    reset({
      name: "",
      type: "" as ApplicationPropertyTypeEnum,
      isRequestable: false,
    })
  }, [reset])

  const onSubmit = (values: ApplicationPropertyPost) => digitalManagerApi
      .post(`/applications/${applicationId}/properties`, {
        name: values.name,
        type: values.type,
        isRequestable: values.isRequestable,
      })
      .then((res) => {
        if (res.status === StatusCodes.CREATED) {
          toast.success("Property created")
          navigate(returnURL)
        }
      })
      .catch(() => {
        toast.error("Cannot create property")
      })

  const handleTextFieldChange = useCallback(
    (field: ControllerRenderProps<ApplicationPropertyPost, "name">) =>
      (value: string) => {
        field.onChange(value.replaceAll(" ", ""))
      },
    []
  )

  const renderNameTextField = useCallback(
    ({ field }: ControllerRendererProps<ApplicationPropertyPost, "name">) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleTextFieldChange]
  )

  const handleTypeChange = useCallback(
    (field: ControllerRenderProps<ApplicationPropertyPost, "type">, values: unknown) => {
      if (Array.isArray(values) && values.length > 0) {
        const selected = typeof values[0] === "string" ? values[0] : values[0].id
        field.onChange(selected)
      }
    },
    []
  )

  const handleTypeSelectChange = useCallback(
    (field: ControllerRenderProps<ApplicationPropertyPost, "type">) =>
      (values: unknown) =>
        handleTypeChange(field, values),
    [handleTypeChange]
  )

  const handleCheckboxToggle = useCallback(
    (field: ControllerRenderProps<ApplicationPropertyPost, "isRequestable">) =>
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderTypeSelect = useCallback(
    ({ field }: ControllerRendererProps<ApplicationPropertyPost, "type">) => (
      <MUISelect
        name={field.name}
        selectedItem={field.value ? [field.value] : []}
        onChange={handleTypeSelectChange(field)}
        dropdownLOV={Object.values(ApplicationPropertyTypeEnum).map((type) => ({
          id: type,
          name: type,
        }))}
        size={32}
        minWidth={250}
        placeholder="Select type"
      />
    ),
    [handleTypeSelectChange]
  )

  const renderCheckbox = useCallback(
    ({ field }: ControllerRendererProps<ApplicationPropertyPost, "isRequestable">) => (
      <MUICheckBox
        id="isRequestable"
        checked={!!field.value}
        handleToggle={handleCheckboxToggle(field)}
      />
    ),
    [handleCheckboxToggle]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Name">
        <Controller name="name" control={control} render={renderNameTextField} />
      </FieldRow>
      <FieldRow label="Type">
        <Controller name="type" control={control} render={renderTypeSelect} />
      </FieldRow>
      <FieldRow label="Is Requestable">
        <Controller name="isRequestable" control={control} render={renderCheckbox} />
      </FieldRow>
    </FormPage>
  )
}
